import React, { useEffect, useMemo } from "react";
import { useAuthSession } from "../../auth/shared";
import { fetchMerchantSubscription } from "../api";
import { useMerchantPlanData } from "../merchant-plan/hooks/useMerchantPlanData";
import { MerchantSubscriptionPayload } from "../types/subscription";

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

const formatDateTime = (value?: string): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const resolvePlanLabel = (
  planId: string | undefined,
  entitlementPlanId?: string,
  entitlementPlanName?: string,
): string => {
  if (!planId) return "-";
  if (entitlementPlanId && entitlementPlanName && planId === entitlementPlanId) {
    return entitlementPlanName;
  }
  return planId;
};

const MerchantSubscriptionCheckPage: React.FC = () => {
  const { user } = useAuthSession();
  const { entitlementProfile, plan, loading, error } = useMerchantPlanData();
  const [subscriptionData, setSubscriptionData] =
    React.useState<MerchantSubscriptionPayload | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = React.useState(false);
  const [subscriptionError, setSubscriptionError] = React.useState<string | null>(null);

  useEffect(() => {
    const loadSubscription = async () => {
      setSubscriptionLoading(true);
      setSubscriptionError(null);
      try {
        const response = await fetchMerchantSubscription();
        if (!response.success || response.http_request_status !== 200) {
          setSubscriptionError(
            response.user_message || response.message || "Failed to fetch merchant subscription",
          );
          return;
        }
        setSubscriptionData(response.data);
      } catch (err: any) {
        setSubscriptionError(
          err?.response?.data?.user_message ||
            err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch merchant subscription",
        );
      } finally {
        setSubscriptionLoading(false);
      }
    };

    loadSubscription();
  }, []);

  const subscriptionHistory = subscriptionData?.data ?? [];
  const latestSubscription = useMemo(() => {
    if (subscriptionHistory.length === 0) return null;
    return [...subscriptionHistory].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    )[0];
  }, [subscriptionHistory]);

  const mismatchChecks = useMemo(() => {
    const checks: string[] = [];

    // if (!user?.subscription_id) {
    //   checks.push("User chưa có `subscription_id` (Stripe subscription id).");
    // }

    if (!entitlementProfile?.subscription?.plan_code) {
      checks.push("Entitlement chưa có `subscription.plan_code`.");
    }

    if (!latestSubscription?.stripe_subscription_id) {
      checks.push("Subscription API chưa có `latest.stripe_subscription_id`.");
    }

    // if (
    //   user?.subscription_id &&
    //   latestSubscription?.stripe_subscription_id &&
    //   user.subscription_id !== latestSubscription.stripe_subscription_id
    // ) {
    //   checks.push(
    //     `Khác subscription_id: auth.user.subscription_id=${user.subscription_id} vs subscriptionApi.latest.stripe_subscription_id=${latestSubscription.stripe_subscription_id}.`,
    //   );
    // }

    if (
      plan?.current_plan &&
      entitlementProfile?.subscription?.plan_code &&
      plan.current_plan.toLowerCase() !== entitlementProfile.subscription.plan_code.toLowerCase()
    ) {
      checks.push(
        `Khác plan: merchantPlan.current_plan=${plan.current_plan} vs entitlement.subscription.plan_code=${entitlementProfile.subscription.plan_code}.`,
      );
    }

    if (
      entitlementProfile?.subscription?.state &&
      latestSubscription?.state &&
      entitlementProfile.subscription.state.toUpperCase() !== latestSubscription.state.toUpperCase()
    ) {
      checks.push(
        `Khác trạng thái: entitlement.subscription.state=${entitlementProfile.subscription.state} vs subscriptionApi.latest.state=${latestSubscription.state}.`,
      );
    }

    return checks;
  }, [entitlementProfile, latestSubscription, plan?.current_plan]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[#0f172a]">
          Merchant vs Stripe Subscription Check
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Trang kiểm tra nhanh dữ liệu subscription giữa merchant profile và entitlement/billing.
        </p>
      </div>

      {(loading || subscriptionLoading) && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
          Đang tải dữ liệu subscription...
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {subscriptionError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {subscriptionError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="text-base font-semibold mb-3">Merchant (Auth User)</h2>
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">merchant_id:</span> {formatValue(user?.id)}
            </p>
            <p>
              <span className="font-medium">email:</span> {formatValue(user?.email)}
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="text-base font-semibold mb-3">Entitlement</h2>
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">state:</span>{" "}
              {formatValue(entitlementProfile?.subscription?.state)}
            </p>
            <p>
              <span className="font-medium">plan_code:</span>{" "}
              {formatValue(entitlementProfile?.subscription?.plan_code)}
            </p>
            <p>
              <span className="font-medium">plan_name:</span>{" "}
              {formatValue(entitlementProfile?.subscription?.plan_name)}
            </p>
            <p>
              <span className="font-medium">plan_id:</span>{" "}
              {formatValue(entitlementProfile?.subscription?.plan_id)}
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-base font-semibold mb-3">Merchant Plan Payload</h2>
        <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-3 overflow-auto">
          {formatValue(plan)}
        </pre>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-base font-semibold mb-3">Merchant Subscription API</h2>
        <div className="space-y-2 text-sm">
          <p>
            <span className="font-medium">merchant_id:</span>{" "}
            {formatValue(subscriptionData?.merchant_id)}
          </p>
          <p>
            <span className="font-medium">latest_subscription_id:</span>{" "}
            {formatValue(latestSubscription?.id)}
          </p>
          <p>
            <span className="font-medium">latest_stripe_subscription_id:</span>{" "}
            {formatValue(latestSubscription?.stripe_subscription_id)}
          </p>
          <p>
            <span className="font-medium">latest_state:</span>{" "}
            {formatValue(latestSubscription?.state)}
          </p>
          <p>
            <span className="font-medium">latest_plan_label:</span>{" "}
            {resolvePlanLabel(
              latestSubscription?.plan_id,
              entitlementProfile?.subscription?.plan_id,
              entitlementProfile?.subscription?.plan_name,
            )}
          </p>
          <p>
            <span className="font-medium">latest_current_period_start:</span>{" "}
            {formatDateTime(latestSubscription?.current_period_start)}
          </p>
          <p>
            <span className="font-medium">latest_current_period_end:</span>{" "}
            {formatDateTime(latestSubscription?.current_period_end)}
          </p>
          <p>
            <span className="font-medium">total_records:</span> {subscriptionHistory.length}
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-base font-semibold mb-3">Subscription History</h2>
        {subscriptionHistory.length === 0 ? (
          <p className="text-sm text-slate-500">Không có lịch sử subscription.</p>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-3">updated_at</th>
                  <th className="py-2 pr-3">state</th>
                  <th className="py-2 pr-3">plan_label</th>
                  <th className="py-2 pr-3">stripe_subscription_id</th>
                  <th className="py-2 pr-3">invoice</th>
                  <th className="py-2 pr-3">period_start</th>
                  <th className="py-2 pr-3">period_end</th>
                </tr>
              </thead>
              <tbody>
                {[...subscriptionHistory]
                  .sort(
                    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
                  )
                  .map((item) => (
                    <tr
                      key={item.id}
                      className="border-b align-top"
                    >
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {formatDateTime(item.updated_at)}
                      </td>
                      <td className="py-2 pr-3">{item.state}</td>
                      <td className="py-2 pr-3 break-all">
                        {resolvePlanLabel(
                          item.plan_id,
                          entitlementProfile?.subscription?.plan_id,
                          entitlementProfile?.subscription?.plan_name,
                        )}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {item.stripe_invoice_url ? (
                          <a
                            href={item.stripe_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                          >
                            Open Invoice
                          </a>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {formatDateTime(item.current_period_start)}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {formatDateTime(item.current_period_end)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantSubscriptionCheckPage;
